import React, { Suspense, useState } from 'react';
import Title from '@/components/title';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import { ReportMenu } from '@/configs/menuItems';
import Card from '@/components/card';
import DoOrderTable from './components/do-report-table';
import { IDoOrder, IDoOrderReq, IDoOrderRes } from '@/interfaces/doOrder';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { searchDoOrders } from '@/adapters/doOrder';
import { Button, DatePicker, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import Search from 'antd/es/input/Search';
import { ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { IDataModal } from '@/interfaces/dataModal';
import DoOrderAddModal from './components/do-add-modal';
import dayjs, { Dayjs } from 'dayjs';
import locale from 'antd/es/date-picker/locale/th_TH';
import ExcelExport from './components/export-report';
import ImportDoOrderModal from '@/components/import/do-order/do-order-modal';

const _criteria: IDoOrderReq = {
  pageSize: 10,
  pageStart: 1,
  startDate: dayjs().add(-2, 'month').startOf('month').toDate(),
  endDate: dayjs().endOf('month').toDate(),
};

const _modal: IDataModal<Partial<IDoOrder>> = {
  open: false,
};

const DoOrderReport: React.FC = () => {
  const pathSegment = getPathSegment();
  const [isImport, setIsImport] = useState<boolean>(false);
  const [criteria, setCriteria] = useState<IDoOrderReq>({
    ..._criteria,
  });
  const [dataModal, setDataModal] = useState<IDataModal<Partial<IDoOrder>>>({
    ..._modal,
  });
  const { data, refetch, isLoading, isFetching } = useQuery<
    AxiosResponse<IDoOrderRes>,
    Error
  >({
    queryKey: ['search-doOrder', criteria],
    queryFn: async () => await searchDoOrders(criteria),
  });

  const onActionInbound = () => {
    refetch();
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IDoOrder> | SorterResult<IDoOrder>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IDoOrder) || newCriteria.orderBy;
      newCriteria.orderType = sorter.order || newCriteria.orderType;
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: '',
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: '',
        pageStart: 1,
        pageSize: 10,
        startDate: undefined,
        endDate: undefined,
      });
    }
  };

  return (
    <>
      <SubMenu active={pathSegment} items={ReportMenu} />
      <Card>
        <Title title="Do Report">
          <div className="flex flex-row gap-2">
            <Button
              ghost
              type="primary"
              onClick={() => {
                setIsImport(true);
              }}
            >
              <ImportOutlined />
              Import DO
            </Button>
            <ExcelExport data={data?.data?.data || []} />
            <Button
              type="primary"
              onClick={() => {
                setDataModal({ open: true });
              }}
            >
              <PlusOutlined />
              เพิ่ม DO ใหม่
            </Button>
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row gap-3">
            <DatePicker.RangePicker
              size="large"
              locale={locale}
              onChange={onRangeChange}
              value={[dayjs(criteria.startDate), dayjs(criteria.endDate)]}
              allowClear={false}
            />
            <Search
              className="md:max-w-[400px] md:min-w-[400px]"
              placeholder="ค้นหา"
              allowClear
              size="large"
              enterButton="ค้นหา"
              onSearch={onSearch}
            />
          </div>
          <DoOrderTable
            criteria={criteria}
            doOrder={data?.data || { data: [], totalRecords: 0 }}
            loading={isLoading || isFetching}
            onEditData={item =>
              setDataModal({
                open: true,
                item: item,
                id: item.id,
              })
            }
            onDelete={onActionInbound}
            onTableChange={onTableChange}
          />
          <Suspense>
            <DoOrderAddModal
              dataModal={dataModal}
              handleOk={function (): void {
                setDataModal({ ..._modal });
                onActionInbound();
              }}
              handleCancel={function (): void {
                setDataModal({ ..._modal });
              }}
            />
            <ImportDoOrderModal
              open={isImport}
              handleOk={() => setIsImport(false)}
              handleCancel={() => setIsImport(false)}
            />
          </Suspense>
        </div>
      </Card>
    </>
  );
};

export default DoOrderReport;
